"use client";

import {
  Providers,
  ClerkApolloBridge,
} from "@workspace/ui/components/providers";
import { InterviewHeader } from "@workspace/ui/components/interview/interview-header";
import { useGuestUserId } from "@/hooks/use-guest-user-id";
import { GuestCookieCleaner } from "@/components/auth/guest-cookie-cleaner";
import { logger } from "@/lib/logger";
import {
  WakeLockProvider,
  useWakeLockControl,
} from "@/components/providers/wake-lock-provider";
import {
  InviteFlowProvider,
  useInviteFlow,
} from "@/contexts/invite-flow-context";
import {
  ConversationProvider,
  useConversationContext,
} from "@/contexts/conversation-context";
import {
  InterviewSheetProvider,
  useInterviewSheet,
} from "@/contexts/interview-sheet-context";
import { ScreenHeightDiv } from "@workspace/ui/components/shared/screen-height-div";
import {
  IndismissableSheet,
  IndismissableSheetContent,
  IndismissableSheetTitle,
  IndismissableSheetVisuallyHidden,
} from "@workspace/ui/components/indismissable-sheet";

function InterviewSheetLayout({ children }: { children: React.ReactNode }) {
  const {
    sheetOpen,
    sheetContent,
    disableAnimations,
    isDismissible,
    closeSheet,
    dismissHandler,
  } = useInterviewSheet();
  const guestUserId = useGuestUserId();
  const { releaseWakeLock } = useWakeLockControl();

  // Get conversation data (fetched via REST, works for all user types)
  const { conversation, loading: conversationLoading } =
    useConversationContext();

  // Get organization branding from invite flow (primary source)
  const { inviteFlowData } = useInviteFlow();

  // Organization info comes from invite flow, connection code from either
  const organizationName = inviteFlowData?.organizationName;
  const organizationAvatarUrl = inviteFlowData?.organizationAvatarUrl;
  const connectionCode =
    conversation?.connectionCode || inviteFlowData?.connectionCode;

  const handlePauseSession = () => {
    // TODO: Implement pause session functionality
    logger.app("Pause session clicked");
  };

  const handleHelp = () => {
    // TODO: Implement help functionality
    logger.app("Help clicked");
  };

  return (
    <>
      {/* Header shown on all routes */}
      <InterviewHeader
        onPauseSession={handlePauseSession}
        onHelp={handleHelp}
        guestUserId={guestUserId}
        releaseWakeLock={releaseWakeLock}
        isSheetOpen={isDismissible}
        onDismissSheet={dismissHandler || closeSheet}
        organizationName={organizationName}
        organizationAvatarUrl={organizationAvatarUrl}
        connectionCode={connectionCode}
        isLoading={
          conversationLoading && !organizationName && !organizationAvatarUrl
        }
      />

      {/* Background layer - full screen */}
      <ScreenHeightDiv className="bg-background p-0 md:p-8 relative">
        <div className="max-w-4xl mx-auto h-full flex flex-col md:space-y-8 relative z-10">
          {/* Render page-specific content (Card with orb/conversation for live, empty for invite) */}
          {children}
        </div>
      </ScreenHeightDiv>

      {/* Shared Bottom Sheet - persists across navigation */}
      <IndismissableSheet open={sheetOpen}>
        <IndismissableSheetContent
          side="bottom"
          className="h-[calc(100dvh-64px)] overflow-y-auto pt-8"
          disableAnimations={disableAnimations}
          aria-describedby={undefined}
        >
          <IndismissableSheetVisuallyHidden asChild>
            <IndismissableSheetTitle>Interview Sheet</IndismissableSheetTitle>
          </IndismissableSheetVisuallyHidden>
          {/* Wrapper that matches InterviewPageContainer structure */}
          <div className="container mx-auto max-w-2xl">{sheetContent}</div>
        </IndismissableSheetContent>
      </IndismissableSheet>
    </>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <Providers>
      <ClerkApolloBridge>
        <GuestCookieCleaner />
        <WakeLockProvider>
          <InviteFlowProvider>
            <ConversationProvider>
              <InterviewSheetProvider>
                <InterviewSheetLayout>{children}</InterviewSheetLayout>
              </InterviewSheetProvider>
            </ConversationProvider>
          </InviteFlowProvider>
        </WakeLockProvider>
      </ClerkApolloBridge>
    </Providers>
  );
}
